import React from 'react';
import { Button, Modal } from 'flowbite-react';
import { BsWhatsapp } from 'react-icons/bs';
import { IoCall, IoMail } from 'react-icons/io5';

export function isPaymentSetupError({ code, message } = {}) {
  if (code === 'COMPANY_GATEWAY_REQUIRED') return true;
  const msg = String(message || '');
  return /payment.*(not set up|not connected|gateway|credentials)|online payments are not|reconnect under payments|razorpay key missing|no payment method available/i.test(
    msg,
  );
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

/** Prefer company contact fields from the chit payload. */
export function resolveCompanyContacts(company) {
  if (!company || typeof company !== 'object') {
    return { name: '', email: '', phone: '', phoneE164: '', phoneDisplay: '' };
  }
  const name = company.companyName || company.basicDetails?.name || '';
  const email =
    company.companyEmail ||
    company.basicDetails?.email ||
    company.email ||
    '';
  const phoneRaw =
    company.companyMobile ||
    company.basicDetails?.mobile ||
    company.mobile ||
    '';
  const digits = digitsOnly(phoneRaw);
  let phoneE164 = '';
  if (digits.length === 10) phoneE164 = `91${digits}`;
  else if (digits.length === 12 && digits.startsWith('91')) phoneE164 = digits;
  else if (digits.length >= 10) phoneE164 = digits;

  const phoneDisplay = phoneE164
    ? phoneE164.startsWith('91') && phoneE164.length === 12
      ? `+91 ${phoneE164.slice(2)}`
      : `+${phoneE164}`
    : '';

  return { name, email, phone: phoneRaw, phoneE164, phoneDisplay };
}

function buildCompanyMessage({
  companyName,
  chitName,
  chitId,
  guestMobile,
} = {}) {
  const lines = [
    `Hi${companyName ? ` ${companyName}` : ''},`,
    '',
    'I tried to Pay & join on ChitBid, but online payments are not set up for your company yet.',
  ];
  if (chitName) lines.push(`Chit: ${chitName}`);
  if (chitId) lines.push(`Chit id: ${chitId}`);
  if (guestMobile) lines.push(`My mobile: +91 ${guestMobile}`);
  lines.push('', 'Please complete payment setup so I can join.');
  return lines.join('\n');
}

/**
 * Shown when Pay & join fails because the company gateway is not ready.
 * Contacts are the **chit company**, not platform Super Admin.
 * Backend also notifies the company admin (payment_setup_required).
 */
const PaymentSetupSupportModal = ({
  isOpen,
  onClose,
  message,
  company,
  chitName,
  chitId,
  guestMobile,
}) => {
  const contacts = resolveCompanyContacts(company);
  const companyName = contacts.name || 'the company';
  const body = buildCompanyMessage({
    companyName: contacts.name,
    chitName,
    chitId,
    guestMobile,
  });
  const subject = encodeURIComponent(
    `Payment setup — ${chitName || 'join'} (${companyName})`,
  );
  const mailBody = encodeURIComponent(body);
  const waText = encodeURIComponent(body);
  const isMobile =
    typeof navigator !== 'undefined' &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const hasPhone = Boolean(contacts.phoneE164);
  const hasEmail = Boolean(contacts.email);
  const whatsappUrl = hasPhone
    ? isMobile
      ? `whatsapp://send?phone=${contacts.phoneE164}&text=${waText}`
      : `https://web.whatsapp.com/send?phone=${contacts.phoneE164}&text=${waText}`
    : null;

  return (
    <Modal show={isOpen} size="md" popup onClose={onClose}>
      <Modal.Body>
        <div className="pt-1 pb-1 font-poppins">
          <h2 className="text-xl font-bold text-[#13224d] mb-2">
            Payments not ready yet
          </h2>
          <p className="text-sm text-[#555555] mb-2">
            {message ||
              'This company has not finished connecting online payments.'}
          </p>
          <p className="text-xs text-[#666666] mb-4 rounded-lg bg-[#FDE6E4]/80 px-3 py-2">
            We&apos;ve notified <strong>{companyName}</strong> to complete
            payment setup. Contact them below if you need help sooner.
          </p>

          {!hasPhone && !hasEmail ? (
            <p className="text-sm text-[#666666] mb-3">
              Company contact details are not available on this chit. Please try
              again later or ask the company for a direct invite.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {whatsappUrl ? (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] text-white text-sm font-semibold px-4 py-2.5 hover:opacity-95"
                >
                  <BsWhatsapp size={18} />
                  WhatsApp {companyName}
                </a>
              ) : null}
              {hasEmail ? (
                <a
                  href={`mailto:${contacts.email}?subject=${subject}&body=${mailBody}`}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#D0D5DD] bg-white text-sm font-semibold text-[#13224d] px-4 py-2.5 hover:bg-[#FEFBFA]"
                >
                  <IoMail size={18} className="text-primaryColor" />
                  Email {contacts.email}
                </a>
              ) : null}
              {hasPhone ? (
                <a
                  href={`tel:+${contacts.phoneE164}`}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#D0D5DD] bg-white text-sm font-semibold text-[#13224d] px-4 py-2.5 hover:bg-[#FEFBFA]"
                >
                  <IoCall size={18} className="text-primaryColor" />
                  Call {contacts.phoneDisplay}
                </a>
              ) : null}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button color="light" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PaymentSetupSupportModal;
